import { useState } from 'react';
import { Container, Title, Table, Button, Group, Modal, TextInput, Stack, ActionIcon, Text, Badge } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash, IconShoppingCart } from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import { useOrderTemplates, useCreateOrderTemplate, useDeleteOrderTemplate, usePlaceOrderFromTemplate } from '@/hooks/usePortalApi';

export default function OrderTemplates() {
  const { data, isLoading } = useOrderTemplates();
  const createMut = useCreateOrderTemplate();
  const deleteMut = useDeleteOrderTemplate();
  const placeMut = usePlaceOrderFromTemplate();
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [orderOpened, { open: openOrder, close: closeOrder }] = useDisclosure(false);
  const [newName, setNewName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);

  const templates = data?.templates ?? [];

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMut.mutate({ name: newName, items: [] }, {
      onSuccess: () => { closeCreate(); setNewName(''); notifications.show({ title: 'Created', message: 'Template created', color: 'green' }); },
    });
  };

  const handleDelete = (name: string) => {
    deleteMut.mutate(name, {
      onSuccess: () => notifications.show({ title: 'Deleted', message: 'Template removed', color: 'red' }),
    });
  };

  const handlePlaceOrder = () => {
    if (!selectedTemplate || !deliveryDate) return;
    placeMut.mutate({ name: selectedTemplate, delivery_date: deliveryDate.toISOString().split('T')[0] }, {
      onSuccess: () => { closeOrder(); notifications.show({ title: 'Order Placed', message: 'Order created from template', color: 'green' }); },
    });
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Order Templates</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Create Template</Button>
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Items</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {templates.map((t) => (
            <Table.Tr key={t.name}>
              <Table.Td>{t.name}</Table.Td>
              <Table.Td><Badge>{t.item_count} items</Badge></Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Button size="xs" variant="light" leftSection={<IconShoppingCart size={14} />}
                    onClick={() => { setSelectedTemplate(t.name); openOrder(); }}>
                    Place Order
                  </Button>
                  <ActionIcon color="red" variant="subtle" onClick={() => handleDelete(t.name)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {templates.length === 0 && !isLoading && (
            <Table.Tr><Table.Td colSpan={3}><Text c="dimmed" ta="center">No templates yet</Text></Table.Td></Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Modal opened={createOpened} onClose={closeCreate} title="Create Template">
        <Stack>
          <TextInput label="Template Name" value={newName} onChange={(e) => setNewName(e.currentTarget.value)} />
          <Button onClick={handleCreate} loading={createMut.isPending}>Create</Button>
        </Stack>
      </Modal>

      <Modal opened={orderOpened} onClose={closeOrder} title={`Place Order: ${selectedTemplate}`}>
        <Stack>
          <DatePickerInput label="Delivery Date" value={deliveryDate} onChange={setDeliveryDate} minDate={new Date()} />
          <Button onClick={handlePlaceOrder} loading={placeMut.isPending}>Place Order</Button>
        </Stack>
      </Modal>
    </Container>
  );
}
